/**
 * Type definitions and mapping functions for Apple Contacts sync.
 *
 * AppleContact mirrors the Rust/Swift serialization format used by
 * the contacts plugin. Two mapping functions handle conversion:
 *
 *   appleContactToContact  (read path: Apple -> Meridian)
 *   contactToAppleContact  (write path: Meridian -> Apple)
 *
 * Apple owns: names, phones, emails, addresses, birthday, jobs,
 * social profiles, dates, relations, notes, and photos.
 * Suron owns: status, move, path, tags, favorite, claimed,
 * connectionStatus, enrichment, and all relational intelligence.
 */
import type { Contact, Phone, Email, Social, Address, Job, Relation, ContactDate } from "@/lib/data";

export interface LabeledValue {
  label: string;
  value: string;
}

export interface PostalAddress {
  label: string;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface SocialProfile {
  service: string;
  username: string;
  url?: string | null;
}

export interface LabeledDate {
  label: string;
  date: string;
}

export interface AppleContact {
  identifier: string;
  givenName: string;
  familyName: string;
  middleName?: string | null;
  namePrefix?: string | null;
  nameSuffix?: string | null;
  nickname?: string | null;
  birthday?: string | null;
  note?: string | null;
  imageData?: string | null;
  phoneNumbers: LabeledValue[];
  emailAddresses: LabeledValue[];
  postalAddresses: PostalAddress[];
  socialProfiles: SocialProfile[];
  dates: LabeledDate[];
  relations: LabeledValue[];
  jobTitle?: string | null;
  departmentName?: string | null;
  organizationName?: string | null;
  urlAddresses: LabeledValue[];
}

/**
 * Convert an Apple Contact into a Meridian Contact.
 * Apple fields map directly. Suron-only fields get sensible defaults
 * and will be populated once the user interacts with the contact.
 */
export function appleContactToContact(apple: AppleContact): Contact {
  return {
    id: apple.identifier,
    appleContactId: apple.identifier,
    appleContactLinked: true,
    firstName: apple.givenName,
    lastName: apple.familyName,
    middleName: apple.middleName ?? undefined,
    namePrefix: apple.namePrefix ?? undefined,
    nameSuffix: apple.nameSuffix ?? undefined,
    nickname: apple.nickname ?? undefined,
    birthday: apple.birthday ?? undefined,
    // Only use thumbnails under 100KB to avoid WKWebView memory pressure
    photo: apple.imageData && apple.imageData.length < 100_000
      ? `data:image/jpeg;base64,${apple.imageData}`
      : undefined,
    bio: undefined,
    phones: apple.phoneNumbers.map(mapLabeledToPhone),
    emails: apple.emailAddresses.map(mapLabeledToEmail),
    socials: apple.socialProfiles.map(mapSocialProfileToSocial),
    addresses: apple.postalAddresses.map(mapPostalToAddress),
    currentCity: apple.postalAddresses[0]?.city ?? undefined,
    currentState: apple.postalAddresses[0]?.state ?? undefined,
    jobs: buildJobs(apple),
    education: [],
    skills: [],
    relations: apple.relations.map(mapLabeledToRelation),
    dates: apple.dates.map(mapLabeledDateToContactDate),
    tags: [],
    status: "connected",
    notes: apple.note ?? "",
    move: null,
    path: null,
    claimed: false,
    source: "apple",
    interactionCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Extract Apple-compatible fields from a Meridian Contact for writing back.
 * Uses null (not undefined) for cleared fields so they serialize in JSON
 * and the Swift writer can detect and clear them in Apple Contacts.
 */
export function contactToAppleContact(contact: Contact): AppleContact {
  const currentJob = contact.jobs.find((j) => j.current);
  return {
    identifier: contact.appleContactId ?? contact.id,
    givenName: contact.firstName,
    familyName: contact.lastName,
    middleName: contact.middleName ?? null,
    namePrefix: contact.namePrefix ?? null,
    nameSuffix: contact.nameSuffix ?? null,
    nickname: contact.nickname ?? null,
    birthday: contact.birthday ?? null,
    note: contact.notes || null,
    phoneNumbers: contact.phones.map((p) => ({ label: p.label, value: p.number })),
    emailAddresses: contact.emails.map((e) => ({ label: e.label, value: e.address })),
    postalAddresses: contact.addresses.map((a) => ({
      label: a.label,
      street: a.street ?? null,
      city: a.city ?? null,
      state: a.state ?? null,
      postalCode: a.zip ?? null,
      country: a.country ?? null,
    })),
    socialProfiles: contact.socials.map((s) => ({
      service: s.platform,
      username: s.handle,
      url: s.url ?? null,
    })),
    dates: (contact.dates ?? []).map((d) => ({ label: d.label, date: d.date })),
    relations: (contact.relations ?? []).map((r) => ({ label: r.label, value: r.name })),
    jobTitle: currentJob?.title ?? null,
    departmentName: null,
    organizationName: currentJob?.company ?? null,
    urlAddresses: contact.socials
      .filter((s) => s.url)
      .map((s) => ({ label: s.platform, value: s.url! })),
  };
}


/* ── Internal mapping helpers ── */

function mapLabeledToPhone(lv: LabeledValue): Phone {
  return { label: lv.label, number: lv.value };
}

function mapLabeledToEmail(lv: LabeledValue): Email {
  return { label: lv.label, address: lv.value };
}

function mapSocialProfileToSocial(sp: SocialProfile): Social {
  return { platform: sp.service, handle: sp.username, url: sp.url ?? undefined };
}

function mapPostalToAddress(pa: PostalAddress): Address {
  return {
    label: pa.label,
    street: pa.street ?? undefined,
    city: pa.city ?? undefined,
    state: pa.state ?? undefined,
    zip: pa.postalCode ?? undefined,
    country: pa.country ?? undefined,
  };
}

function mapLabeledToRelation(lv: LabeledValue): Relation {
  return { label: lv.label, name: lv.value };
}

function mapLabeledDateToContactDate(ld: LabeledDate): ContactDate {
  return { label: ld.label, date: ld.date };
}

/** Build Meridian job entries from Apple's flat job fields. */
function buildJobs(apple: AppleContact): Job[] {
  if (!apple.jobTitle && !apple.organizationName) return [];
  return [
    {
      title: apple.jobTitle ?? "",
      company: apple.organizationName ?? "",
      department: apple.departmentName ?? undefined,
      current: true,
    },
  ];
}
